import { ProfilePath } from '@components/modules/profile/urls';
import { Box } from '@components/ui/atoms/Box';
import { Flex } from '@components/ui/atoms/Flex';
import { Stack } from '@components/ui/atoms/Stack';
import { Text } from '@components/ui/atoms/Text';
import { UserAvatar } from '@components/ui/molecules/UserAvatar';
import { useBreakpointValue } from '@shared/chakra/chakra';
import { useMe } from '@store/entities/me/hooks';
import { MediaEntityType, MediaType } from '@store/entities/media';
import { useMediaByEntityAndType } from '@store/entities/media/hooks/useMediaByEntityAndType';
import { useMeOrg } from '@store/entities/meOrg';
import { useMeRoleNames } from '@store/entities/meRole/hooks/useMeRoleNames';
import { useNavigate } from 'react-router';

type Props = {
  isExpanded?: boolean;
};

export const OnlineUser: React.FC<Props> = ({ isExpanded }) => {
  //TODO: pass these from props
  const navigate = useNavigate();
  const { me } = useMe();
  const { roleNames } = useMeRoleNames();
  const { org } = useMeOrg();
  const { media } = useMediaByEntityAndType(me?.id ?? '', MediaEntityType.User, MediaType.Avatar);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const displayRole = (() => {
    if (!roleNames || roleNames.length === 0) return 'No Role';

    // If any role represents a merchant admin, show a simplified 'Admin' label
    const hasMerchantAdmin = roleNames.some((r) => /merchant\s*admin/i.test(r));

    if (hasMerchantAdmin) return 'Admin';

    return roleNames.join(', ');
  })();

  const handleNavigateToProfile = () => void navigate(ProfilePath);

  return (
    <Flex
      display="flex"
      flexDirection={isMobile ? 'row' : 'column'}
      alignItems="center"
      gap={isMobile ? '16px' : '8px'}
      position="sticky"
      top="0"
      bg="white"
      pb="12px"
      zIndex="2"
    >
      <Box display="flex" alignItems="center" gap="10px">
        <UserAvatar
          onClick={handleNavigateToProfile}
          name={me?.fullName ?? ''}
          avatar={media?.imgixUrl ?? media?.cloudFrontUrl.url}
          size={isExpanded || isMobile ? 'md' : 'sm'}
        />
      </Box>
      {/* Render full details on expand or mobile; render compact on collapsed desktop */}
      {isExpanded || isMobile ? (
        <Stack
          flexDirection={isMobile ? 'row' : 'column'}
          alignItems={isMobile ? 'flex-start' : 'center'}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems={isMobile ? 'flex-start' : 'center'}
            gap={isMobile ? '2px' : '4px'}
          >
            <Text fontWeight="700" fontSize="13px" color="title.dark">
              {me?.fullName ?? 'Guest User'}
            </Text>
            <Text fontSize="12px" fontWeight="400" color="body.dark">
              {org?.name ?? ''}
            </Text>
          </Box>
          <Box
            display="flex"
            padding={isMobile ? '2px 8px' : '4px 12px'}
            alignItems="center"
            gap="8px"
            bg="brand"
            borderRadius="100px"
            alignSelf={isMobile ? 'flex-start' : 'center'}
            maxW={isMobile ? '120px' : '160px'}
          >
            <Text
              fontSize="11px"
              fontWeight="600"
              color="white"
              textTransform="uppercase"
              title={displayRole}
              css={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              flex="1"
              minW={0}
            >
              {displayRole}
            </Text>
          </Box>
        </Stack>
      ) : (
        <Stack alignItems="center">
          <Text fontWeight="700" fontSize="13px" color="title.dark" textAlign="center" maxW="70px">
            {me?.fullName ? me.fullName.split(' ')[0] : 'Guest'}
          </Text>
          <Text
            fontSize="12px"
            fontWeight="400"
            color="body.dark"
            textAlign="center"
            maxW="70px"
            css={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {org?.name ?? ''}
          </Text>
          <Box
            display="flex"
            padding="2px 8px"
            alignItems="center"
            gap="6px"
            bg="brand"
            borderRadius="100px"
            alignSelf="center"
            maxW="70px"
          >
            <Text
              fontSize="11px"
              fontWeight="600"
              color="white"
              textTransform="uppercase"
              title={displayRole}
              css={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              flex="1"
              minW={0}
            >
              {displayRole}
            </Text>
          </Box>
        </Stack>
      )}
    </Flex>
  );
};
